// Deploy-verification bump: harmless change to confirm stale tabs auto-reload.
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

// How often to compare the deployed version against the one this tab loaded.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Grace period shown to a visible tab before the forced reload kicks in.
const RELOAD_DELAY_MS = 5000;

// Polls /version.json (rewritten on every deploy by scripts/generate-version.js)
// and force-reloads the page when a new version is detected, so users can't
// keep running a stale bundle. Hidden tabs reload silently; visible tabs get a
// short toast first so in-progress typing isn't yanked away without warning.
export default function VersionCheck() {
  const initialVersionRef = useRef(null);
  const reloadScheduledRef = useRef(false);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.version || null;
      } catch (e) {
        return null;
      }
    };

    const forceReload = () => {
      if (reloadScheduledRef.current) return;
      reloadScheduledRef.current = true;
      if (document.visibilityState === "hidden") {
        window.location.reload();
        return;
      }
      toast.info("A new version is available. The page will reload in a few seconds.", {
        autoClose: RELOAD_DELAY_MS,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
      });
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    };

    const check = async () => {
      if (reloadScheduledRef.current) return;
      const version = await fetchVersion();
      if (!version) return;
      if (!initialVersionRef.current) {
        initialVersionRef.current = version;
        return;
      }
      if (version !== initialVersionRef.current) {
        forceReload();
      }
    };

    check();
    const timerId = setInterval(check, CHECK_INTERVAL_MS);
    // Stale tabs are usually ones left in the background, so also check the
    // moment the user comes back to the tab.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(timerId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return null;
}
